/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICreateActionContext
} from '@msdyn365-commerce/core';
import { AsyncResult, ChannelInventoryConfiguration, ProductSearchCriteria, ProductSearchResult } from '@msdyn365-commerce/retail-proxy';
import { searchByCriteriaAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { getInventoryConfigurationAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/StoreOperationsDataActions.g';
import { ArrayExtensions, generateProductImageUrl, InventoryLevels } from '@msdyn365-commerce-modules/retail-actions';
import { BaseCollectionInput, createBaseCollectionInput } from './base-collection-action';
import { getInventorySortableRefinerValue } from './get-inventory-refiners';
import _ from 'lodash';
import { getCommercialName } from '../../../shared/functions/get-commercial-name';

/**
 * GetFullProductsByCollection Action Input
 */
export class GetFullProductsByCollectionInput extends BaseCollectionInput implements IActionInput {
    /**
     * The cache object type.
     * @returns The cache object type.
     */
    public getCacheObjectType = (): string => 'FullProductSearchResult';

    /**
     * The data cache type.
     * @returns The data cache type.
     */
    public dataCacheType = (): CacheType => {
        if (
            this.pageType !== 'Category' ||
            (this.refiners && this.refiners.length > 0) ||
            (this.queryResultSettings &&
                this.queryResultSettings.Sorting &&
                this.queryResultSettings.Sorting.Columns &&
                this.queryResultSettings.Sorting.Columns.length > 0)
        ) {
            return 'request';
        }
        return 'application';
    };
}

/**
 * This setting defines inventory filtering options.
 */
export enum ProductListInventoryFilteringOptions {
    /**
     * Filter out all products out of stock.
     */
    HideOOS = 'hideOOS',

    /**
     * Sort products by availability, OOS goes last.
     */
    SortOOS = 'sortOOS',

    /**
     * No filtering selected.
     */
    Default = 'default'
}

export interface ProductResultWithSwatchLimit extends ProductSearchResult {
    additionalSwatches?: number;
    variantRecordId?: number;
}

/**
 * The full product search result with count interface.
 */
interface IFullProductsSearchResultsWithCount {
    products: ProductSearchResult[] | ProductResultWithSwatchLimit[];
    count: number;
    channelInventoryConfigurationId?: number;
    inventoryAwareSortableAttributeId?: number;
}

/**
 * CreateInput function which creates and actionInput used to fetch products for a list page.
 * @param args - The API arguments.
 * @returns IActionInput - The action input.
 */
const createInput = (args: ICreateActionContext<{ itemsPerPage: number; includedAttributes: boolean | undefined }>): IActionInput => {
    const input = createBaseCollectionInput(args, GetFullProductsByCollectionInput);

    // Set Top
    if (input.queryResultSettings.Paging && args.config) {
        input.queryResultSettings.Paging.Top = args.config.itemsPerPage || 1;
    }

    // Set Skip
    if (input.queryResultSettings.Paging && args.requestContext.query && args.requestContext.query.skip) {
        input.queryResultSettings.Paging.Skip = +args.requestContext.query.skip;
    }

    input.queryResultSettings.count = true;

    return input;
};

/**
 * Returns list of products based on inventory information.
 * @param  productSearchResults - The products.
 * @param  context - The context.
 * @param  metadataCount - The metadata count.
 * @param channelInventoryConfiguration - The channel inventory configuration.
 * @returns List of product based on the inventory information.
 */
export async function returnProducts(
    productSearchResults: ProductSearchResult[],
    context: IActionContext,
    input: GetFullProductsByCollectionInput,
    metadataCount: number | undefined,
    channelInventoryConfiguration?: ChannelInventoryConfiguration
): Promise<IFullProductsSearchResultsWithCount> {
    const defaultProductCount: number = 0;

    let productSearchResultsWithImages = productSearchResults.map(productSearchResult => {
        const newImageUrl = generateProductImageUrl(productSearchResult, context.requestContext.apiSettings);

        if (newImageUrl) {
            productSearchResult.PrimaryImageUrl = newImageUrl;
        }

        //Use CommercialStyleName
        if (context.requestContext.app.config?.useCommercialStyleName) {
            productSearchResult.Name = getCommercialName(
                productSearchResult.AttributeValues,
                productSearchResult.Name,
                context.requestContext.app.config?.commercialStyleNameAttribute
            );
        }

        return productSearchResult;
    });

    const searchVariantLimit = context.requestContext.app.config?.searchVariantLimit ?? 1;
    const categoryVariantLimit = context.requestContext.app.config?.categoryVariantLimit ?? 1;
    const variantLimit = input.pageType === 'Category' ? categoryVariantLimit : searchVariantLimit;
    productSearchResultsWithImages = await expandWithVariants(productSearchResultsWithImages, variantLimit);

    // If inventory level is threshold or inventory check is disabled then return the list of products without the inventory configuration
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- read config file.
    if (
        context.requestContext.app.config.inventoryLevel === InventoryLevels.threshold ||
        context.requestContext.app.config.enableStockCheck === false
    ) {
        const result = {
            products: productSearchResultsWithImages,
            count: metadataCount ?? defaultProductCount
        };
        if (typeof window !== 'undefined') {
            window.dispatchEvent(
                new CustomEvent('datalayerEntityLoaded', {
                    detail: {
                        typeName: 'FULLPRODUCTSEARCHRESULT',
                        key: 'FULLPRODUCTSEARCHRESULT',
                        result
                    }
                })
            );
        }
        return result;
    }

    const mappedProducts = productSearchResultsWithImages.map(productSearchResult => {
        if (ArrayExtensions.hasElements(productSearchResult.AttributeValues)) {
            for (const element of productSearchResult.AttributeValues) {
                if (
                    channelInventoryConfiguration &&
                    element.RecordId !== undefined &&
                    element.RecordId === channelInventoryConfiguration.InventoryProductAttributeRecordId &&
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- read config file.
                    context.requestContext.app.config.inventoryRanges !== 'all' &&
                    element.TextValue !== channelInventoryConfiguration.InventoryOutOfStockAttributeValueText
                ) {
                    // If same RecordId then it means that is the Inventory attribute
                    // Based on the inventory range (and filtering options), the inventory label will be displayed
                    // If Inventory range is 'All' then in stock and out of stock labels are shown, else only OOS
                    // if the text value is different that the channelInventoryConfiguration.InventoryOutOfStockAttributeValueText then is in stock
                    element.TextValue = '';
                }
            }
        }

        return productSearchResult;
    });

    const result = {
        products: mappedProducts,
        count: metadataCount ?? defaultProductCount,
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- check config.
        channelInventoryConfigurationId: channelInventoryConfiguration
            ? channelInventoryConfiguration.InventoryProductAttributeRecordId
            : undefined,
        inventoryAwareSortableAttributeId: channelInventoryConfiguration
            ? channelInventoryConfiguration.ProductAvailabilitySortableAttributeRecordId
            : undefined
    };
    if (typeof window !== 'undefined') {
        window.dispatchEvent(
            new CustomEvent('datalayerEntityLoaded', {
                detail: {
                    typeName: 'FULLPRODUCTSEARCHRESULT',
                    key: 'FULLPRODUCTSEARCHRESULT',
                    result
                }
            })
        );
    }
    return result;
}

export async function expandWithVariants(
    mappedProducts: ProductSearchResult[],
    variantLimit: number = 0
): Promise<ProductResultWithSwatchLimit[]> {
    const variantProducts: ProductResultWithSwatchLimit[] = [];
    for (const product of mappedProducts) {
        const colorAttribute = product.AttributeValues?.find(attribute => attribute.Name?.toLowerCase() === 'color');
        if (colorAttribute) {
            let colors: string[] = colorAttribute.TextValue?.split('|') || [];

            if (variantLimit > -1 && colors.length > variantLimit) {
                colors = colors.slice(0, variantLimit);
            }

            for (const color of colors) {
                const prod: ProductResultWithSwatchLimit = _.cloneDeep(product);
                prod.AttributeValues?.forEach(attribute => {
                    if (attribute.Name?.toLowerCase() === 'color') {
                        attribute.Swatches?.forEach(swatch => {
                            swatch.IsDefault = swatch.SwatchValue === color;
                        });
                    }
                });

                variantProducts.push(prod);
            }
        } else {
            variantProducts.push(product);
        }
    }
    return variantProducts;
}

/**
 * Action function to fetch products for a list page.
 * @param input - The input.
 * @param context - The context.
 * @returns IFullProductsSearchResultsWithCount - The full product search result with count.
 */
// eslint-disable-next-line complexity -- ignore the complexity.
async function action(input: GetFullProductsByCollectionInput, context: IActionContext): Promise<IFullProductsSearchResultsWithCount> {
    let promise: AsyncResult<ProductSearchResult[]>;
    let channelInventoryConfigurationPromise: AsyncResult<ChannelInventoryConfiguration>;
    let searchProductId;
    const searchCriteriaInput: ProductSearchCriteria = {};
    searchCriteriaInput.Context = { ChannelId: context.requestContext.apiSettings.channelId, CatalogId: input.catalogId };
    // Decouple searchCriteriaInput.Refinement from input.refiners to avoid input.refiners being modified below
    searchCriteriaInput.Refinement = [...input.refiners];
    searchCriteriaInput.IncludeAttributes = input.includeAttributes;
    searchCriteriaInput.SkipVariantExpansion = true;
    const defaultNumber: number = 0;

    if (input.channelInventoryConfiguration) {
        channelInventoryConfigurationPromise = AsyncResult.resolve(input.channelInventoryConfiguration);
    } else {
        channelInventoryConfigurationPromise = getInventoryConfigurationAsync({ callerContext: context });
    }
    const channelInventoryConfiguration = await channelInventoryConfigurationPromise;

    // Replace InventoryProductAttribute with boolean refiner.
    // Because the value of InventoryProductAttribute is different from language to language since it's string type.
    // The cached old inventory refiner value may be hit when customer switch language which will lead to "no products found" issue.
    const inventoryRefiner = searchCriteriaInput.Refinement.find(
        refiner => refiner.RefinerRecordId === channelInventoryConfiguration?.InventoryProductAttributeRecordId
    );
    if (inventoryRefiner && channelInventoryConfiguration?.InventoryProductAttributeRecordId !== 0) {
        searchCriteriaInput.Refinement = searchCriteriaInput.Refinement.filter(
            refiner => refiner.RefinerRecordId !== channelInventoryConfiguration?.InventoryProductAttributeRecordId
        );

        const isInStockRefiner =
            inventoryRefiner.LeftValueBoundString !== channelInventoryConfiguration?.InventoryOutOfStockAttributeValueText &&
            inventoryRefiner.RightValueBoundString !== channelInventoryConfiguration?.InventoryOutOfStockAttributeValueText;
        const inventoryRefinerValue = getInventorySortableRefinerValue(channelInventoryConfiguration, isInStockRefiner);

        const isInventoryRefinerValueExist = searchCriteriaInput.Refinement.some(
            refiner => refiner.RefinerRecordId === inventoryRefinerValue?.RefinerRecordId
        );
        if (!isInventoryRefinerValueExist && inventoryRefinerValue) {
            searchCriteriaInput.Refinement.push(inventoryRefinerValue);
        }
    }

    if (context.requestContext.app.config?.productListInventoryDisplay === ProductListInventoryFilteringOptions.HideOOS) {
        const isInStock = true;
        const inventoryInStockRefinerValue = getInventorySortableRefinerValue(channelInventoryConfiguration, isInStock);

        const isInventoryInStockRefinerValueExist = searchCriteriaInput.Refinement.some(
            refiner => refiner.RefinerRecordId === inventoryInStockRefinerValue?.RefinerRecordId
        );
        if (!isInventoryInStockRefinerValueExist && inventoryInStockRefinerValue) {
            searchCriteriaInput.Refinement.push(inventoryInStockRefinerValue);
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- app configs are of generic type
    if (
        context.requestContext.app.config?.productListInventoryDisplay === ProductListInventoryFilteringOptions.SortOOS &&
        channelInventoryConfiguration.ProductAvailabilitySortableAttributeRecordId
    ) {
        input.queryResultSettings.Sorting = input.queryResultSettings.Sorting ?? {};
        input.queryResultSettings.Sorting.Columns = input.queryResultSettings.Sorting.Columns ?? [];
        const sortColumnName = `Attr_${channelInventoryConfiguration.ProductAvailabilitySortableAttributeRecordId}`;
        const isSortAttributeExist = input.queryResultSettings.Sorting.Columns.some(column => column.ColumnName === sortColumnName);
        if (!isSortAttributeExist) {
            input.queryResultSettings.Sorting.Columns.push({
                ColumnName: sortColumnName,
                IsDescending: true
            });
        }
    }

    if (input.pageType === 'Category' || context.requestContext.query?.categoryId) {
        if (input.category) {
            searchCriteriaInput.CategoryIds = [input.category || defaultNumber];
            promise = searchByCriteriaAsync(
                {
                    callerContext: context,
                    queryResultSettings: input.queryResultSettings
                },
                searchCriteriaInput
            );
        } else {
            throw new Error('[GetFullProductsForCollection]Category Page Detected, but no global categoryId found');
        }
    } else if (input.searchText && context.requestContext.query?.q) {
        searchCriteriaInput.SearchCondition = input.searchText;
        promise = searchByCriteriaAsync(
            {
                callerContext: context,
                queryResultSettings: input.queryResultSettings
            },
            searchCriteriaInput
        );
    } else if (input.searchText && context.requestContext.query && context.requestContext.query.recommendation) {
        const searchObject = JSON.parse(input.searchText);
        if (context.requestContext.query.productId) {
            searchProductId = Number(searchObject.ProductId);
        }
        if (Number.isNaN(searchProductId)) {
            throw new Error('Failed to cast search product id into a number.');
        } else if (
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- Existing code
            !searchObject.Recommendation
        ) {
            throw new Error('Failed to retrieve the Recommendation.');
        } else {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- Existing code
            searchCriteriaInput.RecommendationListId = searchObject.Recommendation;
            if (searchProductId) {
                searchCriteriaInput.Ids = [searchProductId || defaultNumber];
            }
            promise = searchByCriteriaAsync(
                {
                    callerContext: context,
                    queryResultSettings: input.queryResultSettings
                },
                searchCriteriaInput
            );
        }
    } else {
        throw new Error('[GetFullProductsForCollection]Search Page Detected, but no q= or productId= query parameter found');
    }

    let productSearchResults = await promise;

    const { config: appConfig } = context.requestContext.app;

    const { dimensionToPreSelectInProductCard: swatchToTrim, enableSwatchLimiting = false } = appConfig;

    if (swatchToTrim && enableSwatchLimiting) {
        let swatchLimit: number = 0;
        switch (input.pageType) {
            case 'Category':
                const { swatchLimitCategoryPages } = appConfig;
                swatchLimit = swatchLimitCategoryPages;
                break;
            case 'Search':
                const { swatchLimitSearchPages } = appConfig;
                swatchLimit = swatchLimitSearchPages;
                break;
        }

        const predicateMapper: (input: ProductSearchResult, index: number, list: ProductSearchResult[]) => ProductResultWithSwatchLimit = (
            input,
            index,
            list
        ) => {
            if (input.AttributeValues === undefined) {
                return input;
            }
            const inputAttributeValues = [...input.AttributeValues];
            const attributeToTrim = inputAttributeValues.find(attribute => attribute.KeyName?.toLowerCase() === swatchToTrim);
            if (attributeToTrim === undefined) {
                return input;
            }

            if (attributeToTrim?.Swatches !== undefined && swatchLimit > -1 && attributeToTrim.Swatches.length > swatchLimit) {
                const swatchesToDisplay = attributeToTrim.Swatches.slice(0, swatchLimit);
                const swatchesExcluded = attributeToTrim.Swatches.slice(swatchLimit);

                const indexOfTrimmedAttribute = inputAttributeValues.indexOf(attributeToTrim);

                inputAttributeValues[indexOfTrimmedAttribute].Swatches = swatchesToDisplay;

                return { ...input, additionalSwatches: swatchesExcluded.length + 1 };
            } else {
                return input;
            }
        };
        productSearchResults = productSearchResults.map(predicateMapper);
    }

    return returnProducts(productSearchResults, context, input, promise.metadata.count, channelInventoryConfiguration);
}

export const actionDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/search-result-container/get-full-products-by-collection',
    action: action as IAction<IFullProductsSearchResultsWithCount>,
    input: createInput
});

export default actionDataAction;
